import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { SealCheck, SealWarning, Spinner, CaretDown, CaretRight } from "@phosphor-icons/react";

const BUCKET_STYLES = {
  deliverable: "border-emerald-200 bg-emerald-50 text-emerald-700",
  risky: "border-amber-200 bg-amber-50 text-amber-700",
  undeliverable: "border-rose-200 bg-rose-50 text-rose-700",
  unverified: "border-zinc-200 bg-zinc-50 text-zinc-600",
};

export function VerifyRecipientsCard({ campaignId, editable }) {
  const [sum, setSum] = useState(null);
  const [job, setJob] = useState(null);
  const [showFlagged, setShowFlagged] = useState(false);
  const pollRef = useRef(null);

  const load = () => api.campaignVerification(campaignId).then(setSum).catch(() => {});
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startVerify = async () => {
    const n = sum?.counts?.unverified || 0;
    if (!window.confirm(`Verify ${n.toLocaleString()} unverified address${n === 1 ? "" : "es"} via Hunter?\n\nThis uses 0.5 Hunter credits per address — each address is only ever checked once (results are cached forever).`)) return;
    try {
      const r = await api.verifyStart(campaignId);
      setJob(r);
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.verifyLatest();
          setJob(s.job);
          if (s.job?.status !== "running") {
            clearInterval(pollRef.current);
            if (s.job?.status === "done") {
              const ct = s.job.counters || {};
              toast.success(`Verification complete — ${ct.deliverable || 0} deliverable, ${ct.risky || 0} risky, ${ct.undeliverable || 0} undeliverable`);
            } else {
              toast.error("Verification run did not finish — you can start it again");
            }
            load();
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleExclude = async (bucket) => {
    const cur = new Set(sum?.verification_exclude || []);
    cur.has(bucket) ? cur.delete(bucket) : cur.add(bucket);
    try {
      await api.updateCampaign(campaignId, { verification_exclude: [...cur] });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!sum) return null;
  const { counts } = sum;
  const running = job?.status === "running";
  const excl = sum.verification_exclude || [];
  const skipped = counts.undeliverable + (excl.includes("risky") ? counts.risky : 0);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2.5" data-testid="verify-recipients-card">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 inline-flex items-center gap-1.5">
        <SealCheck size={13} /> Deliverability check <span className="normal-case font-normal">(Hunter — protects your bounce rate)</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="verify-counts">
        {["deliverable", "risky", "undeliverable", "unverified"].map(b => (
          <span key={b} data-testid={`verify-count-${b}`} className={`text-[11px] font-medium px-2 py-1 rounded-full border ${BUCKET_STYLES[b]}`}>
            {(counts[b] || 0).toLocaleString()} {b}
          </span>
        ))}
      </div>

      {running ? (
        <div className="text-xs text-zinc-600 flex items-center gap-1.5" data-testid="verify-progress">
          <Spinner size={13} className="animate-spin" />
          Verifying… {job.progress?.done || 0} of {job.progress?.total || "?"} checked
        </div>
      ) : counts.unverified > 0 ? (
        <button onClick={startVerify} data-testid="verify-recipients-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
          <SealCheck size={13} /> Verify {counts.unverified.toLocaleString()} unverified (0.5 credits each)
        </button>
      ) : (
        <p className="text-xs text-emerald-700">Every recipient has been verified.</p>
      )}

      {(counts.undeliverable > 0 || counts.risky > 0 || excl.length > 0) && (
        <div className="space-y-1.5 pt-1 border-t border-zinc-100">
          {counts.undeliverable > 0 && (
            <p className="text-xs text-zinc-600" data-testid="auto-skip-note">
              <strong>{counts.undeliverable.toLocaleString()} undeliverable</strong> — always skipped automatically at send (they would bounce).
            </p>
          )}
          {editable && (counts.risky > 0 || excl.includes("risky")) && (
            <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" data-testid="exclude-risky-toggle" checked={excl.includes("risky")} onChange={() => toggleExclude("risky")} className="mt-0.5" />
              <span><strong>Skip risky addresses at send</strong> — {counts.risky.toLocaleString()} accept-all/unknown mailboxes that may silently bounce</span>
            </label>
          )}
          {skipped > 0 && <p className="text-[11px] text-zinc-500" data-testid="verify-skip-note">{skipped.toLocaleString()} recipient{skipped === 1 ? "" : "s"} will be skipped when this campaign sends.</p>}
        </div>
      )}

      {(sum.flagged || []).length > 0 && (
        <div className="pt-1 border-t border-zinc-100">
          <button onClick={() => setShowFlagged(v => !v)} data-testid="verify-flagged-toggle"
            className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900">
            {showFlagged ? <CaretDown size={12} /> : <CaretRight size={12} />}
            <SealWarning size={13} className="text-amber-500" /> {sum.flagged.length} flagged address{sum.flagged.length === 1 ? "" : "es"}
          </button>
          {showFlagged && (
            <div className="mt-1.5 max-h-44 overflow-auto space-y-1" data-testid="verify-flagged-list">
              {sum.flagged.map(f => (
                <div key={f.id} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded bg-zinc-50">
                  <span className="truncate text-zinc-700">{f.first_name} {f.last_name} · <span className="font-mono">{f.email}</span></span>
                  <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${BUCKET_STYLES[f.email_verification?.bucket] || BUCKET_STYLES.unverified}`}>
                    {f.email_verification?.bucket}{f.email_verification?.score != null ? ` · ${f.email_verification.score}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
